/**
 * The chrome's icons: the studio rail's rows, and the phone tab bar's five.
 *
 * Hand-inlined rather than an icon dependency, which is the choice `RailIcon`
 * and `UserIcon` in `sidebar.tsx` already made — fourteen marks is not worth a
 * package, and a package would arrive with a house style that is not this
 * one's.
 *
 * Not the design's text glyphs (`▦ ☰ ◎ ◇ ⌇ ⚙`) either, for a reason that only
 * shows up on somebody else's machine: those characters are outside the latin
 * subsets the two webfonts ship, so each one falls through to whatever symbol
 * font the platform happens to have. Their advance widths and optical weights
 * then differ per platform, and a column of icons that does not line up is the
 * opposite of what a design pass is for.
 *
 * One geometry for all of them, and it is the house rule's rather than this
 * file's own: **a 24-unit grid, drawn at 16px, stroked at 1.8**, round caps and
 * joins, `currentColor`. It used to be a 16-unit box at 1.5, which renders a
 * 1.5px line where the design draws a 1.2px one — a visibly heavier column
 * than every other mark in the frame. The paths here are the design's.
 *
 * `--color-*` never appears: an icon takes the colour of the row it sits in,
 * and the only row that overrides it is the selected one, in `SideLink`.
 */
import type { ComponentType, ReactNode } from "react";

export type NavIcon = ComponentType<{ className?: string }>;

function Frame({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {children}
    </svg>
  );
}

/** Games — a shelf of them. */
export function GamesIcon({ className }: { className?: string }) {
  return (
    <Frame className={className}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </Frame>
  );
}

/** Team — the people in it. */
export function TeamIcon({ className }: { className?: string }) {
  return (
    <Frame className={className}>
      <path d="M15.5 19v-1.4a3.4 3.4 0 0 0-3.4-3.4H7.4A3.4 3.4 0 0 0 4 17.6V19" />
      <circle cx="9.7" cy="8" r="3.2" />
      <path d="M20 19v-1.4a3.4 3.4 0 0 0-2.6-3.3" />
    </Frame>
  );
}

/** Plan — what the studio is on, and what it costs. */
export function PlanIcon({ className }: { className?: string }) {
  return (
    <Frame className={className}>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="M3 10h18" />
    </Frame>
  );
}

/** Network — studios connected to studios. */
export function NetworkIcon({ className }: { className?: string }) {
  return (
    <Frame className={className}>
      <circle cx="12" cy="5" r="2.3" />
      <circle cx="5" cy="18" r="2.3" />
      <circle cx="19" cy="18" r="2.3" />
      <path d="M10.6 6.9 6.5 15.8M13.4 6.9l4.1 8.9M7.3 18h9.4" />
    </Frame>
  );
}

/** Integrations — two links of a chain, joined. */
export function IntegrationsIcon({ className }: { className?: string }) {
  return (
    <Frame className={className}>
      <path d="M10.5 13.5a4 4 0 0 1 0-5.7l2.3-2.3a4 4 0 0 1 5.7 5.7l-1.2 1.2" />
      <path d="M13.5 10.5a4 4 0 0 1 0 5.7l-2.3 2.3a4 4 0 0 1-5.7-5.7l1.2-1.2" />
    </Frame>
  );
}

/** Provider spend — a stack of coins, counted rather than added up. */
export function SpendIcon({ className }: { className?: string }) {
  return (
    <Frame className={className}>
      <ellipse cx="12" cy="6" rx="7.5" ry="2.8" />
      <path d="M4.5 6v5.5c0 1.55 3.36 2.8 7.5 2.8s7.5-1.25 7.5-2.8V6" />
      <path d="M4.5 11.5V17c0 1.55 3.36 2.8 7.5 2.8s7.5-1.25 7.5-2.8v-5.5" />
    </Frame>
  );
}

/** Settings — the knobs, not a cog: a cog at 16px is mush. */
export function SettingsIcon({ className }: { className?: string }) {
  return (
    <Frame className={className}>
      <path d="M4 7h9M18.5 7H20M4 17h3.5M13 17h7" />
      <circle cx="15.5" cy="7" r="2.3" />
      <circle cx="10" cy="17" r="2.3" />
    </Frame>
  );
}

/** Sign out — a door with the way out. */
export function SignOutIcon({ className }: { className?: string }) {
  return (
    <Frame className={className}>
      <path d="M10 4H6.5A1.5 1.5 0 0 0 5 5.5v13A1.5 1.5 0 0 0 6.5 20H10" />
      <path d="M15 8l4 4-4 4M19 12H10" />
    </Frame>
  );
}

/*
 * The five below are the phone tab bar's, and they exist only there.
 *
 * The rail draws no marks beside a project's destinations — five group
 * headings already do that job, and twenty-four glyphs nobody designed would
 * be twenty-four guesses. A tab bar has no headings and no room for a full
 * label, so there the mark is carrying the row rather than decorating it.
 */

/** Overview — the roof over everything in the game. */
export function HomeIcon({ className }: { className?: string }) {
  return (
    <Frame className={className}>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5.5 10.5V20h13v-9.5" />
    </Frame>
  );
}

/** Live events — a trace of what is arriving. */
export function EventsIcon({ className }: { className?: string }) {
  return (
    <Frame className={className}>
      <path d="M3 12h4l3-7 4 14 3-7h4" />
    </Frame>
  );
}

/** Observe — an eye on the running game. */
export function ObserveIcon({ className }: { className?: string }) {
  return (
    <Frame className={className}>
      <path d="M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="2.8" />
    </Frame>
  );
}

/** Analytics — three rising bars, for a place that has numbers to show. */
export function ChartIcon({ className }: { className?: string }) {
  return (
    <Frame className={className}>
      <path d="M5 20v-6M12 20V9M19 20V4" />
    </Frame>
  );
}

/** Usage — what has been counted, as three bars. */
export function UsageIcon({ className }: { className?: string }) {
  return (
    <Frame className={className}>
      <path d="M5 20V12M12 20V5M19 20v-5" />
      <path d="M3 20h18" />
    </Frame>
  );
}

/** Players — one of them. */
export function PlayersIcon({ className }: { className?: string }) {
  return (
    <Frame className={className}>
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5 20v-1.4A3.6 3.6 0 0 1 8.6 15h6.8a3.6 3.6 0 0 1 3.6 3.6V20" />
    </Frame>
  );
}

/** Economy — what is held, at three heights. */
export function EconomyIcon({ className }: { className?: string }) {
  return (
    <Frame className={className}>
      <path d="M5 20V10M12 20V4M19 20v-7" />
    </Frame>
  );
}

/** More — everything the four beside it left in the drawer. */
export function MoreIcon({ className }: { className?: string }) {
  return (
    <Frame className={className}>
      <path d="M5 12h.01M12 12h.01M19 12h.01" />
    </Frame>
  );
}

/** The MCP mark: a window with a code sign. Drawn at 1.6, as the boards draw it. */
export function McpIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="none">
      <rect
        x="2.5"
        y="4"
        width="19"
        height="16"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path
        d="m8.5 9-3 3 3 3m7-6 3 3-3 3m-2-7-3 8"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Six dots in two columns: what to take hold of to drag. */
export function GripIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      className={className ?? "size-4 shrink-0"}
    >
      <circle cx="9" cy="6" r="1.5" />
      <circle cx="15" cy="6" r="1.5" />
      <circle cx="9" cy="12" r="1.5" />
      <circle cx="15" cy="12" r="1.5" />
      <circle cx="9" cy="18" r="1.5" />
      <circle cx="15" cy="18" r="1.5" />
    </svg>
  );
}
